import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { fetchQuotaData } from "../../utils/apis";
import { Menu, Text, Flex, Divider, useMantineTheme } from "@mantine/core";
import { useStylesHeader } from "../../utils/mantine/useStylesHeader";
import moment from "moment";

const QuotaLimitHeader = ({ updateStampQuota }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css Classes
  const { classes: headerClasses } = useStylesHeader();

  const formattedDateTime = (date) =>
    moment(date, "YYYY-MM-DD hh:mm:ss").format("MM/DD/YYYY");

  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [userBalanceInfo, setUserBalanceInfo] = useState("");
  const [subscriptionInfo, setSubscriptionInfo] = useState("");
  const { title: planTitle = "" } = subscriptionPlan;
  const { next_billed_at = "", is_subscription_active = false } =
    subscriptionInfo;
  const {
    expirable_credits = 0,
    paid_credits = 0,
    total_credits = 0,
    next_renewal_at = "",
  } = userBalanceInfo;

  // Fetch quota helper (stable reference)
  const fetchQuota = useCallback(async () => {
    try {
      const { subscriptionPlan, userBalanceInfo, subscriptionInfo } =
        await fetchQuotaData();
      setSubscriptionPlan(subscriptionPlan);
      setUserBalanceInfo(userBalanceInfo);
      setSubscriptionInfo(subscriptionInfo);
    } catch (error) {
      // noop
    }
  }, []);

  // Stable event handler reference to avoid leaks
  const handleQuotaRefresh = useCallback(() => {
    fetchQuota();
  }, [fetchQuota]);

  // Initial load and event listener setup (run once)
  useEffect(() => {
    fetchQuota();
    window.addEventListener("quota:refresh", handleQuotaRefresh);
    return () =>
      window.removeEventListener("quota:refresh", handleQuotaRefresh);
  }, [handleQuotaRefresh]);

  // React to external update triggers
  useEffect(() => {
    if (updateStampQuota) {
      fetchQuota();
    }
  }, [updateStampQuota, fetchQuota]);

  return (
    <Menu
      shadow="md"
      openDelay={100}
      closeDelay={400}
      trigger="hover"
      transitionProps={{ transition: "fade-down", duration: 150 }}
      className={headerClasses.quotaLimitButton}
    >
      <Menu.Target className={headerClasses.menuButtonUser}>
        <Text className="user-menu-name">{total_credits} stamps available</Text>
      </Menu.Target>

      <Menu.Dropdown
        className={headerClasses.menu + " " + headerClasses.quotaLimit}
      >
        <Text pb={theme.padding.smEm} className="quotaStrong">
          Current Stamp Balance:
        </Text>

        <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
          <Text component="span">Monthly Free Stamp Credits:</Text>
          <Text component="span" className="quotaStrong">
            {expirable_credits}
          </Text>
        </Flex>
        <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
          <Text component="span">Purchased Stamp Credits:</Text>
          <Text component="span" className="quotaStrong">
            {paid_credits}
          </Text>
        </Flex>
        <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
          <Text component="span">Total Stamp Credits:</Text>
          <Text component="span" className="quotaStrong">
            {total_credits}
          </Text>
        </Flex>
        <Divider />
        <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
          <Text component="span">Plan:</Text>
          <Text component="span" className="quotaStrong">
            {planTitle}
          </Text>
        </Flex>
        {next_billed_at && (
          <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
            <Text component="span">Next billing date:</Text>
            <Text component="span" className="quotaStrong">
              {formattedDateTime(next_billed_at)}
            </Text>
          </Flex>
        )}
        {!is_subscription_active && next_renewal_at && (
          <Flex gap={theme.flex.gap.xs} pb={theme.padding.smEm}>
            <Text component="span">Next renewal date:</Text>
            <Text component="span" className="quotaStrong">
              {formattedDateTime(next_renewal_at)}
            </Text>
          </Flex>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

QuotaLimitHeader.propTypes = {
  currentScreen: PropTypes.string,
  updateStampQuota: PropTypes.bool,
};

export default QuotaLimitHeader;
